import { useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { removeFamilyVideo, useFamilyLessonVideos, type FamilyVideo } from '../api/family-lesson-videos.js';
import { day, dayAndTime, length } from './video-format.js';
import { RemoveVideoSheet } from './remove-video-sheet.js';
import { VideoPlayer } from './video-player.js';

/**
 * The videos on a child's lesson, on the parent's lesson page (step 7b; canvas
 * row 4, approved 17 October):
 *
 *   - "From Coach Matthew": the coach's own videos, to play, never removed by
 *     the parent;
 *   - "Noa's videos": each with its length, when it was sent, when the coach
 *     opened it, Noa's note and the coach's reply, and Remove - opened or not,
 *     for as long as it is shown (ruled 17 October). Remove opens the warning
 *     panel (remove-video-sheet.tsx).
 *
 * Nothing at all when the lesson has no videos, and nothing if the list cannot
 * be read: the rest of the page stands on its own.
 */
export function FamilyVideos({
  lessonId,
  childFirst,
  coachName,
}: {
  lessonId: string;
  childFirst: string;
  coachName: string;
}) {
  const getToken = useAccessToken();
  const [nonce, setNonce] = useState(0);
  const state = useFamilyLessonVideos(lessonId, nonce);
  const [playing, setPlaying] = useState<{ url: string; label: string } | null>(null);
  const [removing, setRemoving] = useState<FamilyVideo | null>(null);
  const [working, setWorking] = useState(false);
  const [failed, setFailed] = useState(false);

  if (state.status !== 'ready') return null;
  const { videos, coach_videos: fromCoach } = state.data;
  if (videos.length === 0 && fromCoach.length === 0) return null;

  const coach = coachName ? `Coach ${coachName.split(' ')[0]}` : 'Coach';

  async function remove() {
    if (!removing || working) return;
    setWorking(true);
    setFailed(false);
    try {
      await removeFamilyVideo(getToken, lessonId, removing.id);
      setRemoving(null);
      setNonce((n) => n + 1);
    } catch {
      setFailed(true);
    } finally {
      setWorking(false);
    }
  }

  return (
    <>
      {fromCoach.length > 0 ? (
        <>
          <span className="section-label">From {coach}</span>
          {fromCoach.map((video) => (
            <div className="video-row" key={video.id}>
              <button
                type="button"
                className="thumb"
                aria-label={`Play ${coach}'s video from ${day(video.sent_at)}`}
                onClick={() => setPlaying({ url: video.video_url, label: `${coach}'s video, ${day(video.sent_at)}` })}
              >
                {video.thumbnail_url ? <img src={video.thumbnail_url} alt="" /> : null}
                <span className="dur">{length(video.duration_seconds)}</span>
              </button>
              <div className="hour-row-text">
                <span className="name">{dayAndTime(video.sent_at)}</span>
              </div>
            </div>
          ))}
        </>
      ) : null}

      {videos.length > 0 ? (
        <>
          <span className="section-label">{childFirst}&rsquo;s videos</span>
          {videos.map((video) => (
            <div className="video-row" key={video.id}>
              <button
                type="button"
                className="thumb"
                aria-label={`Play ${childFirst}'s video from ${day(video.sent_at)}`}
                onClick={() =>
                  setPlaying({ url: video.video_url, label: `${childFirst}'s video, ${day(video.sent_at)}` })
                }
              >
                {video.thumbnail_url ? <img src={video.thumbnail_url} alt="" /> : null}
                <span className="dur">{length(video.duration_seconds)}</span>
              </button>
              <div className="hour-row-text">
                <span className="name">{dayAndTime(video.sent_at)}</span>
                {video.opened_at ? <span className="detail">Opened {day(video.opened_at)}</span> : null}
              </div>
              <div className="actions">
                <button
                  type="button"
                  onClick={() => {
                    setFailed(false);
                    setRemoving(video);
                  }}
                >
                  Remove
                </button>
              </div>
              {video.athlete_note || video.coach_reply ? (
                <div className="video-note">
                  {video.athlete_note ? (
                    <div className="note-read">
                      <span className="from">{childFirst}</span>
                      <p>{video.athlete_note}</p>
                    </div>
                  ) : null}
                  {video.coach_reply ? (
                    <div className="note-read">
                      <span className="from">{coach}</span>
                      <p>{video.coach_reply}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </>
      ) : null}

      {playing ? <VideoPlayer url={playing.url} label={playing.label} onClose={() => setPlaying(null)} /> : null}

      {removing ? (
        <RemoveVideoSheet
          video={removing}
          childFirst={childFirst}
          working={working}
          failed={failed}
          onRemove={() => void remove()}
          onKeep={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}
